import { useRef, useState } from "react";
import Layout from "../components/Layout";
import CountdownTimer from "../components/CountdownTimer";
import ShareButton from "../components/ShareButton";
import DownloadImageButton from "../components/DownloadImageButton";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Play, RotateCcw, Sparkles } from "lucide-react";

const COLOR = "#22C55E";
const DURATIONS = [60, 90, 120];

const ObjetoImposible = () => {
  const [duration, setDuration] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState("idle");
  const cardRef = useRef(null);

  const generate = async (d = duration) => {
    setLoading(true);
    try {
      const res = await api.post("/generate/objeto-imposible", { duration: d });
      setData(res.data);
      setPhase("ready");
    } catch {
      toast.error("La IA tropezó. Reintenta.");
    } finally {
      setLoading(false);
    }
  };

  const start = () => setPhase("running");
  const finish = () => setPhase("done");
  const reset = () => { setPhase("idle"); setData(null); };

  const objeto = data?.data?.objeto;
  const contexto = data?.data?.contexto;
  const twist = data?.data?.twist;

  return (
    <Layout accent={COLOR}>
      <section className="max-w-5xl mx-auto px-5 sm:px-8 pt-10 sm:pt-16">
        <div className="mb-8">
          <span className="inline-block font-mono text-xs uppercase tracking-[0.25em] font-bold bg-[#22C55E] border-4 border-[#1a1a1a] rounded-full px-4 py-1 mb-4">
            tool · 04 · nuevo
          </span>
          <h1 className="font-display font-black text-5xl sm:text-7xl leading-[0.95] tracking-tight mb-4">
            objeto <span className="text-[#22C55E]">imposible</span>.
          </h1>
          <p className="font-mono text-sm sm:text-base text-[#4a4a4a] max-w-xl">
            La IA mezcla 2 cosas que no van juntas y le inventa un drama. Dibújalo en el tiempo dado.
          </p>
        </div>

        {phase === "idle" && (
          <div className="bg-white border-4 border-[#1a1a1a] rounded-3xl shadow-hard-md p-8 sm:p-10 max-w-2xl pop-in" data-testid="objeto-setup">
            <div className="font-mono text-xs uppercase tracking-[0.25em] font-bold mb-3">duración</div>
            <div className="flex flex-wrap gap-3 mb-8">
              {DURATIONS.map((d) => (
                <button
                  key={d}
                  data-testid={`obj-duration-${d}`}
                  onClick={() => setDuration(d)}
                  className={`press border-4 border-[#1a1a1a] rounded-2xl px-6 py-4 font-display font-black text-2xl shadow-hard-sm ${
                    duration === d ? "bg-[#22C55E]" : "bg-white"
                  }`}
                >
                  {d}s
                </button>
              ))}
            </div>
            <button
              data-testid="generate-objeto-btn"
              onClick={() => generate(duration)}
              disabled={loading}
              className="press w-full bg-[#1a1a1a] text-white border-4 border-[#1a1a1a] rounded-2xl px-6 py-5 font-display font-black text-2xl shadow-hard-md disabled:opacity-60 inline-flex items-center justify-center gap-2"
            >
              <Sparkles className="w-6 h-6" strokeWidth={3} />
              {loading ? "Inventando..." : "generar objeto imposible"}
            </button>
          </div>
        )}

        {phase === "ready" && data && (
          <div ref={cardRef} className="bg-white border-4 border-[#1a1a1a] rounded-3xl shadow-hard-lg p-8 sm:p-12 pop-in relative" data-testid="objeto-ready">
            <div className="absolute top-3 right-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#737373] font-bold">
              guango · objeto imposible
            </div>
            <div className="font-mono text-xs uppercase tracking-[0.25em] font-bold mb-4 text-[#737373]">tu objeto</div>
            <h2 className="font-display font-black text-4xl sm:text-6xl leading-[1.05] tracking-tight mb-4">
              {objeto}
            </h2>
            <p className="font-mono text-base sm:text-xl text-[#4a4a4a] mb-4 leading-relaxed">{contexto}</p>
            {twist && (
              <div className="bg-[#22C55E] border-4 border-[#1a1a1a] rounded-2xl px-5 py-3 inline-block font-mono text-sm sm:text-base font-bold mb-8 tilt-1">
                ✦ {twist}
              </div>
            )}
            <div className="flex flex-wrap gap-3" data-exclude-from-image="true">
              <button
                data-testid="start-objeto-btn"
                onClick={start}
                className="press inline-flex items-center gap-2 bg-[#22c55e] border-4 border-[#1a1a1a] rounded-xl px-6 py-3 font-display font-black text-xl shadow-hard-md"
              >
                <Play className="w-5 h-5" strokeWidth={3} /> dibujar {duration}s
              </button>
              <button
                data-testid="objeto-regenerate-btn"
                onClick={() => generate(duration)}
                disabled={loading}
                className="press inline-flex items-center gap-2 bg-white border-4 border-[#1a1a1a] rounded-xl px-6 py-3 font-mono text-sm uppercase tracking-wider font-bold shadow-hard-sm"
              >
                otro objeto
              </button>
              <ShareButton promptId={data.id} color={COLOR} testId="objeto-share" />
              <DownloadImageButton
                targetRef={cardRef}
                fileName={`guango-objeto-imposible-${data.id.slice(0, 8)}.png`}
                color={COLOR}
                testId="objeto-download"
              />
            </div>
          </div>
        )}

        {phase === "running" && (
          <div data-testid="objeto-running">
            <div className="bg-[#1a1a1a] text-white border-4 border-[#1a1a1a] rounded-2xl px-5 py-3 inline-block font-mono text-xs uppercase tracking-[0.25em] font-bold mb-6 max-w-full">
              dibujando: {objeto}
            </div>
            <CountdownTimer duration={duration} color={COLOR} running onComplete={finish} />
          </div>
        )}

        {phase === "done" && (
          <div className="bg-[#22C55E] border-4 border-[#1a1a1a] rounded-3xl shadow-hard-lg p-8 sm:p-12 pop-in" data-testid="objeto-done">
            <div className="font-display font-black text-5xl sm:text-7xl mb-4">¡fin!</div>
            <p className="font-mono text-base sm:text-lg mb-2">
              Tu objeto era: <span className="font-bold">{objeto}</span>
            </p>
            <p className="font-mono text-sm sm:text-base mb-8 text-[#1a1a1a]/80">{contexto}</p>
            <div className="flex flex-wrap gap-3">
              <button
                data-testid="objeto-another-btn"
                onClick={() => generate(duration)}
                disabled={loading}
                className="press inline-flex items-center gap-2 bg-[#1a1a1a] text-white border-4 border-[#1a1a1a] rounded-xl px-6 py-3 font-display font-black text-xl shadow-hard-md"
              >
                <RotateCcw className="w-5 h-5" strokeWidth={3} /> otro objeto
              </button>
              <button
                data-testid="objeto-reset-btn"
                onClick={reset}
                className="press inline-flex items-center gap-2 bg-white border-4 border-[#1a1a1a] rounded-xl px-6 py-3 font-mono text-sm uppercase tracking-wider font-bold shadow-hard-sm"
              >
                cambiar duración
              </button>
            </div>
          </div>
        )}
      </section>
    </Layout>
  );
};

export default ObjetoImposible;
